import { Pencil, Trash2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/shadcn/button'
import { Badge } from '@/components/shadcn/badge'
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/shadcn/table'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/shadcn/tooltip'
import { LoaderTable } from '@/components/common/loaders/LoaderTable'
import { ActionModuleIcon } from '@/pages/groups/components/ActionModuleIcon'
import { ShopModuleIcon, shopModuleLabel } from '@/pages/groups/components/ShopModuleIcon'
import { ModuleIconList, MAX_VISIBLE } from '@/pages/groups/components/ModuleIconList'
import type { DetailedWalletTypeResponse } from '@/api/model'

interface Props {
  isLoading: boolean
  isError: boolean
  walletTypes: DetailedWalletTypeResponse[]
  onEdit: (walletType: DetailedWalletTypeResponse) => void
  onDelete: (walletType: DetailedWalletTypeResponse) => void
}

export function WalletTypesDataTable({ isLoading, isError, walletTypes, onEdit, onDelete }: Props) {
  const { t } = useTranslation('wallet-types')

  return (
    <Table>
      <TableHeader className="sticky top-0 z-10 bg-card shadow-sm [&_th]:bg-card">
        <TableRow>
          <TableHead>{t('list.columns.name')}</TableHead>
          <TableHead>{t('list.columns.description')}</TableHead>
          <TableHead>{t('list.columns.fallback')}</TableHead>
          <TableHead>{t('list.columns.actionModules')}</TableHead>
          <TableHead>{t('list.columns.shopModules')}</TableHead>
          <TableHead className="w-20" />
        </TableRow>
      </TableHeader>
      <TableBody>
        <LoaderTable isLoading={isLoading} isError={isError} isEmpty={walletTypes.length === 0} colSpan={6}>
          {walletTypes.map((wt) => (
            <TableRow key={wt.id}>
              <TableCell className="font-medium">{wt.name ?? '—'}</TableCell>
              <TableCell>
                {wt.description
                  ? wt.description
                  : <span className="text-muted-foreground text-sm">—</span>}
              </TableCell>
              <TableCell>
                {wt.isFallback && <Badge>{t('list.fallbackBadge')}</Badge>}
              </TableCell>

              <TableCell>
                <ModuleIconList
                  restLabels={( wt.actionModules ?? []).slice(MAX_VISIBLE).map((a) => a.name ?? '—')}
                >
                  {( wt.actionModules ?? []).map((a) => (
                    <ActionModuleIcon
                      key={a.id}
                      module={{
                        name: a.name,
                        iconName: a.iconName,
                        iconUrl: a.iconUrl ?? null,
                        colorHex: a.colorHex,
                      }}
                    />
                  ))}
                </ModuleIconList>
              </TableCell>

              <TableCell>
                <ModuleIconList
                  restLabels={(wt.shopModules ?? []).slice(MAX_VISIBLE).map((s) => shopModuleLabel(s.type))}
                >
                  {(wt.shopModules ?? []).map((s) => (
                    <ShopModuleIcon key={s.id} type={s.type} />
                  ))}
                </ModuleIconList>
              </TableCell>

              <TableCell>
                <div className="flex items-center gap-1">
                  <Button variant="ghost" size="icon" onClick={() => onEdit(wt)}>
                    <Pencil className="size-4" />
                  </Button>
                  {wt.isFallback ? (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span tabIndex={0}>
                          <Button variant="ghost" size="icon" disabled className="text-destructive">
                            <Trash2 className="size-4" />
                          </Button>
                        </span>
                      </TooltipTrigger>
                      <TooltipContent>{t('list.cannotDeleteFallback')}</TooltipContent>
                    </Tooltip>
                  ) : (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-destructive hover:text-destructive"
                      onClick={() => onDelete(wt)}
                    >
                      <Trash2 className="size-4" />
                    </Button>
                  )}
                </div>
              </TableCell>
            </TableRow>
          ))}
        </LoaderTable>
      </TableBody>
    </Table>
  )
}
